import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { DATE_FORMAT_DEFAUT, DefaultAssetImage, DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import dayjs from 'dayjs';
import DriversService from 'api/drivers/DriversService';
import { DriversListItemDto } from 'api/drivers/models/DriversListItemDto';
import { DriversTotalsDto } from 'api/drivers/models/DriversTotalsDto';
import { DriversSearchCriteria } from 'api/drivers/models/DriversSearchCriteria';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import Logger from 'common/services/Logger';
import Loading from 'common/services/Loading';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Popover from 'common/components/popover/Popover';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';
import { CompactPageLayout, compactPageLayoutStyles } from 'common/components/compactPageLayout';
import DataTableWithSidebar from 'common/components/dataTable/DataTableWithSidebar';
import { DataTableColumn, OrderData } from 'common/components/dataTable/models';
import InfoCard from 'common/components/infoCard/InfoCard';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import colors from 'styles/export/colors.module.scss';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import CoinsIcon from 'assets/svg/coins.svg';
import TicketIcon from 'assets/svg/ticket_white.svg';
import ConeIcon from 'assets/svg/cone.svg';
import { FaEllipsisH } from 'react-icons/fa';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';
import DriversFilters, { Filters } from './components/DriversFilters';
import VatNotice from 'common/components/vatNotice/VatNotice';
import styles from './DriversScreen.module.scss';

const FILTERS_CACHE_KEY = 'DRIVERSFILTER';

const ORDER_COLUMN_TO_FIELD: Record<string, keyof DriversListItemDto> = {
    'u.real_name': 'name',
    'u.email': 'email',
    'driver_verifications.vehicle_registration_number': 'vehicleRegistrationNumber',
    'driver_verifications.vehicle_brand_name': 'vehicleBrandName',
    'driver_verifications.vehicle_model_name': 'vehicleModelName',
    'refoundsMade': 'refoundsMade',
    'refundsMissing': 'refundsMissing',
    'total_cost_refunds_denied': 'totalCostRefundsDenied',
    'coalesce(uCharges.total,0)': 'totalCostFines',
    'coalesce(uAccidents.total,0)': 'accidents',
};

const INFO_CARD_COLORS = [
    colors.counterTeal1,
    colors.counterTeal2,
    colors.counterBlue1,
    colors.counterBlue2,
    colors.counterBlue3,
];

const DriversScreen = () => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const imageCacheKey = useRef(new Date().getTime());
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const [criteria, setCriteria] = useState<DriversSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'u.real_name',
        orderBy: 'asc'
    });

    const [drivers, setDrivers] = useState<DriversListItemDto[]>([]);
    const history = useHistory();
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);
    const [totals, setTotals] = useState<DriversTotalsDto>({ averageKilometers: 0, fines: 0, totalAccidents: 0, refoundsMade: 0, refundsMissing: 0, refundsNotPayed: 0, refundsPending: 0, totalCostRefundsDenied: 0 });
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasUsersWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_USERS_WRITE']);

    const [itemToRemove, setItemToRemove] = useState<DriversListItemDto | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const isFirstLoad = useRef(true);
    const [reRenderSearch, setReRenderSearch] = useState<number>(0);

    // Bidirectional scroll hook
    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: FILTERS_CACHE_KEY,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = bidirectionalScroll;

    const countersLoaded = useRef<boolean>(false);
    const previousCriteria = useRef<DriversSearchCriteria | null>(null);
    const isLoadingData = useRef<boolean>(false);

    // Order state for DataTable
    const [order, setOrder] = useState<OrderData<DriversListItemDto>>({
        field: 'name',
        orderColumn: 'u.real_name',
        isOrderAsc: true,
    });

    const getData = async (isLoadMore = false, shouldUpdateCounters = false) => {
        if (isLoadingData.current) {
            return;
        }
        isLoadingData.current = true;

        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            saveOrdernationFilters();

            let totalsDB = null;
            if (shouldUpdateCounters) {
                totalsDB = await DriversService.getTotals(criteria);
            }

            const driversDB = await DriversService.getList(criteria);
            setTotalItems(driversDB.totalItems);

            if (isLoadMore) {
                setDrivers(prev => [...prev, ...driversDB.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setDrivers(driversDB.items);
            }

            if (totalsDB) {
                setTotals({ ...totalsDB });
                countersLoaded.current = true;
            }

            // Handle scroll restoration
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(driversDB.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...criteria, page: currentPage - 1 };
                        const prevResult = await DriversService.getList(prevCriteria);
                        setDrivers(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.DRIVERS, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...criteria, page: currentPage + 1 };
                        const nextResult = await DriversService.getList(nextCriteria);
                        setDrivers(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.DRIVERS, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.DRIVERS, `Couldn't get drivers list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            resetScrollState();
        } finally {
            Loading.hide();
            setIsLoadingMore(false);
            isLoadingData.current = false;
        }
    };

    // Handler for infinite scroll load more
    const loadMore = useCallback(() => {
        if (isLoadingData.current || isLoadingMore || drivers.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, drivers.length, totalItems, loadedPageRange.max]);

    // Load previous pages (bidirectional scroll)
    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();
            const prevCriteria: DriversSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await DriversService.getList(prevCriteria);

            setDrivers(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.DRIVERS, `Couldn't get previous drivers page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    // Adjust scroll position after prepending items
    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [drivers, isLoadingPrevious, adjustScrollAfterPrepend]);

    // Handler de ordenação
    const handleOrderChange = (orderData: OrderData<DriversListItemDto>) => {
        setOrder(orderData);
        setDrivers([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({
            ...prev,
            page: 1,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
            orderColumn: orderData.orderColumn || orderData.field as string,
        }));
    };

    const showRemoveItemDialog = (item: DriversListItemDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            await UsersService.remove(itemToRemove as any);
            setDrivers([]);
            resetScrollState();
            setTotalItems(0);
            setCriteria(prev => ({ ...prev, page: 1 }));
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete charge`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    const tableColumns: DataTableColumn<DriversListItemDto>[] = useMemo(() => [
        // Condutor (nome + avatar)
        {
            field: 'name' as keyof DriversListItemDto,
            name: t('drivers.list.driver') as string,
            orderColumn: 'u.real_name',
            width: 180,
            renderCell: row => (
                <div className={styles.imageDescCell}>
                    <AvatarWithText src={row.photoUrl} size="x2" hideAvatar={!row.name}>
                        <div className={styles.bold}>{row.name || '-'}</div>
                    </AvatarWithText>
                </div>
            ),
        },
        // Email
        {
            field: 'email' as keyof DriversListItemDto,
            name: t('common.email') as string,
            orderColumn: 'u.email',
            width: 180,
            renderCell: row => <>{row.email || '-'}</>,
        },
        // Foto Veículo
        {
            field: 'vehiclePhotoUrl' as keyof DriversListItemDto,
            name: t('common.image') as string,
            hideOrderButton: true,
            width: 90,
            excludeFromExport: true,
            renderCell: row => (
                <div className={styles.contentImage} style={{ width: '4rem', height: '3rem' }}>
                    {!!row.vehicleRegistrationNumber && <div className={styles.img} style={{ width: '4rem', height: '3rem', backgroundImage: row?.vehiclePhotoUrl ? 'url(' + row?.vehiclePhotoUrl + '?_=' + imageCacheKey.current + ')' : row?.vehicleSegmentDefaultPhotoUrl ? 'url(' + row?.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />}
                </div>
            ),
        },
        // Matrícula
        {
            field: 'vehicleRegistrationNumber' as keyof DriversListItemDto,
            name: t('vehicle.registration') as string,
            orderColumn: 'driver_verifications.vehicle_registration_number',
            width: 130,
            renderCell: row => <div className={styles.columnPlate}>{row.vehicleRegistrationNumber || '-'}</div>,
        },
        // Marca
        {
            field: 'vehicleBrandName' as keyof DriversListItemDto,
            name: t('vehicle.brand') as string,
            orderColumn: 'driver_verifications.vehicle_brand_name',
            width: 100,
            renderCell: row => <>{row.vehicleBrandName || '-'}</>,
        },
        // Modelo
        {
            field: 'vehicleModelName' as keyof DriversListItemDto,
            name: t('vehicle.model') as string,
            orderColumn: 'driver_verifications.vehicle_model_name',
            width: 100,
            renderCell: row => <>{row.vehicleModelName || '-'}</>,
        },
        // Reembolsos Feitos
        {
            field: 'refoundsMade' as keyof DriversListItemDto,
            name: t('drivers.list.refunds') as string,
            orderColumn: 'refoundsMade',
            width: 120,
            headerNote: `${t('common.unit')}: ${t('common.euro')}`,
            renderCell: row => <MoneyFormat value={row.refoundsMade ? row.refoundsMade : 0} fixedDecimalScale={true} />,
        },
        // Reembolsos Pendentes
        {
            field: 'refundsMissing' as keyof DriversListItemDto,
            name: t('drivers.list.refunds_pending') as string,
            orderColumn: 'refundsMissing',
            width: 150,
            headerNote: `${t('common.unit')}: ${t('common.euro')}`,
            exportValue: row => row.refundsMissing ?? 0,
            renderCell: row => <><MoneyFormat value={row.refundsMissing ? row.refundsMissing : 0} fixedDecimalScale={true} /> ({row.refundsNotPayed ?? 0}/{row.refundsPending ?? 0})</>,
        },
        // Reembolsos Não Aprovados
        {
            field: 'totalCostRefundsDenied' as keyof DriversListItemDto,
            name: t('drivers.list.refunds_not_approved') as string,
            orderColumn: 'total_cost_refunds_denied',
            width: 130,
            headerNote: `${t('common.unit')}: ${t('common.euro')}`,
            renderCell: row => <MoneyFormat value={row.totalCostRefundsDenied ? row.totalCostRefundsDenied : 0} fixedDecimalScale={true} />,
        },
        // Multas
        {
            field: 'totalCostFines' as keyof DriversListItemDto,
            name: t('drivers.list.fines') as string,
            orderColumn: 'coalesce(uCharges.total,0)',
            width: 120,
            headerNote: `${t('common.unit')}: ${t('common.euro')}`,
            exportValue: row => row.totalCostFines ?? 0,
            renderCell: row => <><MoneyFormat value={row.totalCostFines ? row.totalCostFines : 0} fixedDecimalScale={true} /> ({row.fines ? row.fines : 0})</>,
        },
        // Acidentes
        {
            field: 'accidents' as keyof DriversListItemDto,
            name: t('drivers.list.accidents') as string,
            orderColumn: 'coalesce(uAccidents.total,0)',
            width: 100,
            renderCell: row => <>{row.accidents ? row.accidents : 0}</>,
        },
    ], [t, hasUsersWritePolicy]);

    const renderTableActionCell = (row: DriversListItemDto) => {
        const urlDetails = `drivers/details/${row.id}`;
        const urlEdit = `drivers/edit/${row.id}`;
        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={urlDetails}>
                            {t('common.details')}
                        </DropdownItem>
                        {hasUsersWritePolicy && <DropdownItem url={urlEdit}>
                            {t('common.edit')}
                        </DropdownItem>}
                        {hasUsersWritePolicy && <DropdownItem onClick={() => showRemoveItemDialog(row)}>
                            {t('common.remove')}
                        </DropdownItem>}
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const navigateTo = (type?: string, id?: string) => {
        if (id) {
            history.push(`/drivers/${type}/${id}/user/list`);
        } else {
            history.push(`/drivers/${type}`);
        }
    }

    const onRowClick = (row: DriversListItemDto, index: number, event: any) => {
        if (row.id) {
            event.preventDefault();
            event.stopPropagation();

            const actualPage = calculatePageFromIndex(index);

            const newFilters = { ...filters };
            newFilters.page = actualPage;
            (newFilters as any).scrollToItemId = row.id;
            newFilters.orderBy = criteria.orderBy;
            newFilters.orderColumn = criteria.orderColumn;
            saveFiltersOnCache(newFilters);

            if (event.ctrlKey) {
                window.open('drivers/details/' + row.id, '_blank');
                return;
            }
            navigateTo('details', row.id);
        }
    };

    const saveOrdernationFilters = () => {
        const newFilters = { ...filters };
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);
    };

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(f);
        if ((f as any).scrollToItemId && f.page) {
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));
        } else {
            const { page: _page, scrollToItemId: _scrollToItemId, ...filtersWithoutScrollData } = f as Filters & { scrollToItemId?: string };
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(filtersWithoutScrollData));
        }
    };

    useEffect(() => {
        if (isFirstLoad.current === true) {
            loadFilters();
        } else {
            const isLoadMore = criteria.page > loadedPageRange.max && drivers.length > 0;

            const prev = previousCriteria.current;
            const onlyOrderOrPageChanged = prev &&
                prev.driverId === criteria.driverId &&
                prev.vehicleId === criteria.vehicleId &&
                prev.minRefunds === criteria.minRefunds &&
                prev.maxRefunds === criteria.maxRefunds &&
                prev.minRefundsMissing === criteria.minRefundsMissing &&
                prev.maxRefundsMissing === criteria.maxRefundsMissing &&
                prev.minRefundsDenied === criteria.minRefundsDenied &&
                prev.maxRefundsDenied === criteria.maxRefundsDenied &&
                prev.minFines === criteria.minFines &&
                prev.maxFines === criteria.maxFines &&
                prev.minAccidents === criteria.minAccidents &&
                prev.maxAccidents === criteria.maxAccidents &&
                prev.startDate === criteria.startDate &&
                prev.endDate === criteria.endDate;

            const shouldLoadCounters = !countersLoaded.current || !onlyOrderOrPageChanged;
            getData(isLoadMore, shouldLoadCounters);
            previousCriteria.current = { ...criteria };
        }
    }, [criteria]);

    const loadFilters = () => {
        isFirstLoad.current = false;

        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null) {
            const f = JSON.parse(cachedFilters);

            if (f.scrollToItemId) {
                const startPage = initiateRestoration({
                    page: f.page || 1,
                    scrollToItemId: f.scrollToItemId,
                });

                const cleanedFilters = { ...f, scrollToItemId: undefined, page: 1 };
                localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(cleanedFilters));
                setFilters(cleanedFilters);
                updateTotalFilters(cleanedFilters);

                setCriteria(prev => ({
                    ...prev,
                    driverId: f.driverId,
                    minRefunds: f.refunds && f.refunds[0],
                    maxRefunds: f.refunds && f.refunds[1],
                    minRefundsMissing: f.refundsMissing && f.refundsMissing[0],
                    maxRefundsMissing: f.refundsMissing && f.refundsMissing[1],
                    minRefundsDenied: f.refundsDenied && f.refundsDenied[0],
                    maxRefundsDenied: f.refundsDenied && f.refundsDenied[1],
                    minFines: f.fines && f.fines[0],
                    maxFines: f.fines && f.fines[1],
                    minAccidents: f.accidents && f.accidents[0],
                    maxAccidents: f.accidents && f.accidents[1],
                    vehicleId: f.vehicleId,
                    startDate: f.startDate,
                    endDate: f.endDate,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn,
                    page: startPage,
                    itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                }));

                if (f.orderBy && f.orderColumn) {
                    setOrder({
                        field: ORDER_COLUMN_TO_FIELD[f.orderColumn] || f.orderColumn as keyof DriversListItemDto,
                        orderColumn: f.orderColumn,
                        isOrderAsc: f.orderBy === 'asc',
                    });
                }
                return;
            }

            f.scrollToItemId = undefined;
            f.page = 1;
            resetScrollState();
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));

            onChangeFilters(f, true);
        } else {
            onChangeFilters({}, true);
        }
    };

    const onChangeFilters = (f: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(f);
        saveFiltersOnCache(f);

        if (!isOnLoad) {
            setDrivers([]);
            resetScrollState();
            setTotalItems(0);
        }

        setCriteria(prev => ({
            ...prev,
            driverId: f.driverId,
            minRefunds: f.refunds && f.refunds[0],
            maxRefunds: f.refunds && f.refunds[1],
            minRefundsMissing: f.refundsMissing && f.refundsMissing[0],
            maxRefundsMissing: f.refundsMissing && f.refundsMissing[1],
            minRefundsDenied: f.refundsDenied && f.refundsDenied[0],
            maxRefundsDenied: f.refundsDenied && f.refundsDenied[1],
            minFines: f.fines && f.fines[0],
            maxFines: f.fines && f.fines[1],
            minAccidents: f.accidents && f.accidents[0],
            maxAccidents: f.accidents && f.accidents[1],
            vehicleId: f.vehicleId,
            startDate: f.startDate,
            endDate: f.endDate,
            orderBy: f.orderBy,
            orderColumn: f.orderColumn,
            page: 1,
        }));

        if (isOnLoad && f.orderBy && f.orderColumn) {
            setOrder({
                field: ORDER_COLUMN_TO_FIELD[f.orderColumn] || f.orderColumn as keyof DriversListItemDto,
                orderColumn: f.orderColumn,
                isOrderAsc: f.orderBy === 'asc',
            });
        }
        setReRenderSearch(r => r + 1);
    };

    const updateTotalFilters = (f: Filters) => {
        let total = 0;
        if (f.startDate || f.endDate) {
            total++;
        }
        if (f.driverId) {
            total++;
        }
        if (f.vehicleId) {
            total++;
        }
        if (f.fines && (f.fines[0] || f.fines[1])) {
            total++;
        }
        if (f.accidents && (f.accidents[0] || f.accidents[1])) {
            total++;
        }
        if (f.refunds && (f.refunds[0] || f.refunds[1])) {
            total++;
        }
        if (f.refundsMissing && (f.refundsMissing[0] || f.refundsMissing[1])) {
            total++;
        }
        if (f.refundsDenied && (f.refundsDenied[0] || f.refundsDenied[1])) {
            total++;
        }

        setFiltersTotal(total);
    }

    // Build filter badge items for FilterBadgesOverflow
    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (filters.vehicleName) {
            items.push({
                key: 'vehicle',
                label: t('drivers.list.vehicle') as string,
                value: filters.vehicleName,
                onRemove: () => onChangeFilters({ ...filters, vehicleId: undefined, vehicleName: undefined })
            });
        }

        if (filters.driverName) {
            items.push({
                key: 'driver',
                label: t('common.driver') as string,
                value: filters.driverName,
                onRemove: () => onChangeFilters({ ...filters, driverId: undefined, driverName: undefined })
            });
        }

        if (filters.refunds?.filter(x => x !== undefined).length) {
            let value = '';
            if (filters.refunds.filter(i => i !== undefined).length === 2) {
                value = `${filters.refunds[0]} - ${filters.refunds[1]}`;
            } else if (filters.refunds[0] !== undefined) {
                value = `> ${filters.refunds[0]}`;
            } else {
                value = `< ${filters.refunds[1]}`;
            }
            items.push({
                key: 'refunds',
                label: t('drivers.list.refunds') as string,
                value,
                onRemove: () => onChangeFilters({ ...filters, refunds: undefined })
            });
        }

        if (filters.refundsMissing?.filter(x => x !== undefined).length) {
            let value = '';
            if (filters.refundsMissing.filter(i => i !== undefined).length === 2) {
                value = `${filters.refundsMissing[0]} - ${filters.refundsMissing[1]}`;
            } else if (filters.refundsMissing[0] !== undefined) {
                value = `> ${filters.refundsMissing[0]}`;
            } else {
                value = `< ${filters.refundsMissing[1]}`;
            }
            items.push({
                key: 'refundsMissing',
                label: t('drivers.list.refunds_pending') as string,
                value,
                onRemove: () => onChangeFilters({ ...filters, refundsMissing: undefined })
            });
        }

        if (filters.refundsDenied?.filter(x => x !== undefined).length) {
            let value = '';
            if (filters.refundsDenied.filter(i => i !== undefined).length === 2) {
                value = `${filters.refundsDenied[0]} - ${filters.refundsDenied[1]}`;
            } else if (filters.refundsDenied[0] !== undefined) {
                value = `> ${filters.refundsDenied[0]}`;
            } else {
                value = `< ${filters.refundsDenied[1]}`;
            }
            items.push({
                key: 'refundsDenied',
                label: t('drivers.list.refunds_not_approved') as string,
                value,
                onRemove: () => onChangeFilters({ ...filters, refundsDenied: undefined })
            });
        }

        if (filters.fines?.filter(x => x !== undefined).length) {
            let value = '';
            if (filters.fines.filter(i => i !== undefined).length === 2) {
                value = `${filters.fines[0]} - ${filters.fines[1]}`;
            } else if (filters.fines[0] !== undefined) {
                value = `> ${filters.fines[0]}`;
            } else {
                value = `< ${filters.fines[1]}`;
            }
            items.push({
                key: 'fines',
                label: t('drivers.filter.fines') as string,
                value,
                onRemove: () => onChangeFilters({ ...filters, fines: undefined })
            });
        }

        if (filters.accidents?.filter(x => x !== undefined).length) {
            let value = '';
            if (filters.accidents.filter(i => i !== undefined).length === 2) {
                value = `${filters.accidents[0]} - ${filters.accidents[1]}`;
            } else if (filters.accidents[0] !== undefined) {
                value = `> ${filters.accidents[0]}`;
            } else {
                value = `< ${filters.accidents[1]}`;
            }
            items.push({
                key: 'accidents',
                label: t('drivers.filter.accidents') as string,
                value,
                onRemove: () => onChangeFilters({ ...filters, accidents: undefined })
            });
        }

        if (filters.startDate || filters.endDate) {
            const start = filters.startDate ? dayjs(filters.startDate).format(DATE_FORMAT_DEFAUT) : '';
            const end = filters.endDate ? dayjs(filters.endDate).format(DATE_FORMAT_DEFAUT) : '';
            items.push({
                key: 'dates',
                label: t('drivers.filter.date') as string,
                value: start && end ? `${t('common.range.from')} ${start} ${t('common.range.to')} ${end}` : start || end,
                onRemove: () => onChangeFilters({ ...filters, startDate: undefined, endDate: undefined })
            });
        }

        return items;
    }, [filters, t]);

    const renderCounterBoxes = () => (
        <div className={compactPageLayoutStyles.rowCounters}>
            <InfoCard
                label={t('drivers.list.refunds') as string}
                value={totals?.refoundsMade ?? 0}
                icon={CoinsIcon}
                backgroundColor={INFO_CARD_COLORS[0]}
                isMoney
            />
            <InfoCard
                label={t('drivers.list.refunds_pending') as string}
                subtitle={`${t('drivers.list.refunds_pending_tooltip')}: ${totals?.refundsNotPayed ?? 0}/${totals?.refundsPending ?? 0}`}
                value={totals?.refundsMissing ?? 0}
                icon={CoinsIcon}
                backgroundColor={INFO_CARD_COLORS[1]}
                isMoney
            />
            <InfoCard
                label={t('drivers.list.refunds_not_approved') as string}
                value={totals?.totalCostRefundsDenied ?? 0}
                icon={CoinsIcon}
                backgroundColor={INFO_CARD_COLORS[2]}
                isMoney
            />
            <InfoCard
                label={t('drivers.list.total_fines') as string}
                subtitle={`Nº: ${totals?.fines ?? 0}`}
                value={totals?.totalCostFines ?? 0}
                icon={TicketIcon}
                backgroundColor={INFO_CARD_COLORS[3]}
                isMoney
            />
            <InfoCard
                label={t('drivers.list.total_accidents') as string}
                value={totals?.totalAccidents ?? 0}
                icon={ConeIcon}
                backgroundColor={INFO_CARD_COLORS[4]}
            />
        </div>
    );

    const fetchAllForExport = useCallback(async () => {
        const result = await DriversService.getList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria]);

    return (
        <ScreenTitle title={t('drivers.title')}>
            <CompactPageLayout
                title={t('drivers.title') as string}
                contentAreaClassName={styles.contentArea}
                middleContent={<FilterBadgesOverflow items={filterItems} align="right" />}
                headerButtons={
                    <>
                        <Popover
                            contentContainerClassName={styles.filtersPopoverContainer}
                            containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                            positions={['bottom', 'left']}
                            align={'end'}
                            onClickOutside={() => updateTotalFilters(filters)}
                            content={setIsPopoverOpen => <DriversFilters
                                filters={filters}
                                onFilter={f => { setIsPopoverOpen(false); onChangeFilters(f) }}
                                onChange={f => updateTotalFilters(f)}
                            />}>
                            {(isPopoverOpen, setIsPopoverOpen) => (
                                <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                    {filtersTotal > 0 &&
                                        <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                    }
                                </ScreenHeaderButton>
                            )}
                        </Popover>
                        <ExportListingButton exportRef={exportListingRef} />
                        {hasUsersWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => navigateTo('new')} />}
                    </>
                }
            >
                {totals && (
                    <div className={compactPageLayoutStyles.countersSection}>
                        <VatNotice />
                        {renderCounterBoxes()}
                    </div>
                )}

                <div className={compactPageLayoutStyles.listTblWrapper}>
                    {isFirstLoad.current === false && (
                        <DataTableWithSidebar
                            key={reRenderSearch}
                            cacheKey="DRIVERS_TABLE"
                            columns={tableColumns}
                            rows={drivers}
                            defaultOrder={order}
                            onOrderChange={handleOrderChange}
                            onRowClick={(row, index, event) => onRowClick(row, index, event)}
                            onHref={row => `/drivers/details/${row.id}/user/list`}
                            onRenderAction={renderTableActionCell}
                            onRowId={row => row.id ?? ''}
                            scrollToRowId={scrollToItemId}
                            infiniteScroll={{
                                isLoading: isLoadingMore,
                                hasMore: getHasMore(drivers.length, totalItems),
                                onLoadMore: getOnLoadMore(loadMore),
                                hasMorePrevious: getHasMorePrevious(),
                                isLoadingPrevious: isLoadingPrevious,
                                onLoadPrevious: getOnLoadPrevious(loadPrevious),
                            }}
                            exportRef={exportListingRef}
                            hideExportInSidebar={true}
                            exportExcel={{
                                fetchAll: fetchAllForExport,
                                fileName: t('drivers.title') as string,
                            }}
                        />
                    )}
                </div>
            </CompactPageLayout>
            <QuestionYesNo
                onNo={onCancelRemove}
                onYes={onRemove}
                isVisible={showRemoveModal}
                message={t('common.messages.remove_record_with_ident', { name: itemToRemove?.name ?? '' })} />
        </ScreenTitle>
    );
}

export default DriversScreen;
